import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import CompanyUserAssignment from '../components/CompanyUserAssignment'
import { getCompanyUsers, assignUserToCompany } from '../services/companySyncService'
import LoadingIndicator from '../components/LoadingIndicator'
import Alert from '../components/Alert'
import '../styles/CompanySyncPage.css'

// Default project ID to fetch companies and users from
// Change this to your desired project ID or make it configurable
const DEFAULT_PROJECT_ID = '12f1c1c7-f0f6-450e-9127-6ba52ce3b78b'

/**
 * CompanySync Page
 * Page for managing company-user assignments
 */
const CompanySyncPage = ({ setIsSidebarOpen }) => {
  const { token } = useAuth()
  const { showSuccess, showError } = useToast()

  const [companies, setCompanies] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const selectedProjectId = DEFAULT_PROJECT_ID

  // Fetch companies and users on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setError('No authentication token found. Please log in.')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Fetch users (we know this works)
        console.log('[CompanySyncPage] Fetching users...')
        const usersResponse = await getCompanyUsers(selectedProjectId, {
          limit: 100,
          offset: 0,
          sort: 'name asc',
          orFilters: 'email,name',
          filterName: '',
          filterEmail: ''
        })

        console.log('[CompanySyncPage] Users loaded:', {
          projectId: selectedProjectId,
          count: usersResponse.users.length,
          pagination: usersResponse.pagination
        })

        setUsers(usersResponse.users)

        // Hardcoded company data (from HQ API response format)
        console.log('[CompanySyncPage] Using hardcoded company data...')
        const hardcodedCompanyResponse = {
          "pagination": {
            "limit": 200,
            "offset": 0,
            "totalResults": 2
          },
          "results": [
            {
              "id": "ee17a16a-f3fb-4ec1-9c98-908e7a72fd2a",
              "name": "HOCON",
              "trade": null,
              "addresses": [
                {
                  "type": "Main",
                  "addressLine1": null,
                  "addressLine2": null,
                  "city": null,
                  "stateOrProvince": null,
                  "postalCode": null,
                  "country": "US",
                  "phone": null
                }
              ],
              "websiteUrl": null,
              "description": null,
              "erpId": null,
              "taxId": null,
              "accountId": "7deef331-7e4e-4607-9ef2-3cf726158332",
              "imageUrl": "https://bim360-ea-ue1-prod-storage.s3.amazonaws.com/companies/images/default/medium/company-default-1.0.png",
              "createdAt": "2023-02-05T19:49:08.942Z",
              "updatedAt": "2026-06-24T07:33:09.976Z",
              "originalName": null,
              "projectSize": 19,
              "userSize": 13,
              "status": "active",
              "category": "owner"
            },
            {
              "id": "d4f996d6-fe02-42f3-af90-0c95f9998af7",
              "name": "V+S Design",
              "trade": null,
              "addresses": [
                {
                  "type": "Main",
                  "addressLine1": null,
                  "addressLine2": null,
                  "city": null,
                  "stateOrProvince": null,
                  "postalCode": null,
                  "country": "US",
                  "phone": null
                }
              ],
              "websiteUrl": null,
              "description": null,
              "erpId": null,
              "taxId": null,
              "accountId": "7deef331-7e4e-4607-9ef2-3cf726158332",
              "imageUrl": "https://bim360-ea-ue1-prod-storage.s3.amazonaws.com/companies/images/default/medium/company-default-1.0.png",
              "createdAt": "2023-04-26T10:23:11.121Z",
              "updatedAt": "2026-01-06T07:49:15.727Z",
              "originalName": null,
              "projectSize": 1,
              "userSize": 0,
              "status": "active",
              "category": null
            }
          ]
        }

        // Transform company data to UI format
        const companiesData = hardcodedCompanyResponse.results.map(company => ({
          id: company.id,
          name: company.name,
          icon: '🏢',
          trade: company.trade,
          imageUrl: company.imageUrl,
          status: company.status,
          category: company.category,
          projectSize: company.projectSize,
          userSize: company.userSize,
          accountId: company.accountId,
          addresses: company.addresses,
          websiteUrl: company.websiteUrl,
          description: company.description,
          createdAt: company.createdAt,
          updatedAt: company.updatedAt,
          _isHardcoded: true
        }))

        console.log('[CompanySyncPage] Companies loaded:', {
          count: companiesData.length,
          companies: companiesData.map(c => ({
            name: c.name,
            id: c.id,
            projectSize: c.projectSize,
            userSize: c.userSize
          }))
        })

        setCompanies(companiesData)

        console.log('[CompanySyncPage] Data loaded successfully:', {
          companies: companiesData.length,
          users: usersResponse.users.length,
          projectId: selectedProjectId
        })
      } catch (err) {
        console.error('[CompanySyncPage] Error loading data:', err)
        setError(err.message || 'Failed to load data')
        showError('Failed to load companies and users')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [token, selectedProjectId])

  const handleAssignment = async (company, user) => {
    try {
      console.log('[CompanySyncPage] Assigning user to company:', {
        projectId: selectedProjectId,
        company: company.name,
        companyId: company.id,
        user: user.name,
        userId: user.id
      })

      await assignUserToCompany(selectedProjectId, user.id, company.id)

      showSuccess(`Successfully assigned ${user.name} to ${company.name}`)

      // Refresh the user list from the current project to show updated assignments
      const usersResponse = await getCompanyUsers(selectedProjectId, {
        limit: 100,
        offset: 0,
        sort: 'name asc',
        orFilters: 'email,name',
        filterName: '',
        filterEmail: ''
      })
      setUsers(usersResponse.users)
    } catch (err) {
      console.error('[CompanySyncPage] Assignment failed:', err)
      showError(err.message || 'Failed to assign user to company')
    }
  }

  return (
    <div className="company-sync-page">
      <div className="page-header">
        <button
          className="menu-toggle"
          onClick={() => setIsSidebarOpen && setIsSidebarOpen(prev => !prev)}
          aria-label="Toggle menu"
        >
          ☰
        </button>
        <h1>Company User Assignment</h1>
      </div>

      <div className="page-content">
        {loading ? (
          <LoadingIndicator message="Loading companies and users..." />
        ) : error ? (
          <Alert
            type="error"
            title="Error Loading Data"
            message={error}
          />
        ) : companies.length === 0 ? (
          <Alert
            type="info"
            title="No Companies Found"
            message="You don't have access to any companies. Please contact your administrator."
          />
        ) : (
          <CompanyUserAssignment
            companies={companies}
            users={users}
            onAssign={handleAssignment}
          />
        )}
      </div>
    </div>
  )
}

export default CompanySyncPage
